import { DeclarationPage } from 'plantItemNew/constants/declarationPage';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';
import { PlantItemNewOngoingContactTypes as OngoingTypes } from 'plantItemNew/constants/plantItemConstants';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { RefData } from 'plantItemNew/constants/refData';
import { PrivacyCollectionNotice } from 'shared/constants/privacyCollectionNotice';
import { RegistrationHolderTypes } from 'shared/constants/registrationHolderTypes';
import { formatBlankFields, formatDeclarations, formatSummaryLabels } from 'shared/utils/FormatHelper';
import { capitalizeFirstLetter, formatAddress, getFullname } from '../../shared/utils/Helpers';
import { getContactDetailsSubmission } from './PlantItemNewFormatHelper';

export const formatApplicationSummary = (applicantDetails, holderDetails, plantItems, declarations) => {
  const sections = [];
  sections.push(formatDetailsOwnershipSection(applicantDetails, holderDetails));
  sections.push(...formatItemsSection(plantItems));

  formatSummaryLabels(sections);

  const formattedDeclarations = formatDeclarations(
    DeclarationPage.consent,
    declarations.stamp,
    getFullname(applicantDetails.firstName, applicantDetails.middleName, applicantDetails.lastName),
    applicantDetails.jobTitle,
  );

  return { sections, declarations: formattedDeclarations, privacyCollection: PrivacyCollectionNotice };
};

const formatDetailsOwnershipSection = (applicantDetails, holderDetails) => {
  let ownershipSection = { title: 'Your details and item ownership', subSections: [] };

  ownershipSection.subSections.push(formatItemRegistrationHolder(holderDetails));
  ownershipSection.subSections.push(formatOngoingContact(applicantDetails, holderDetails));

  return ownershipSection;
};

const formatItemRegistrationHolder = (holderDetails) => {
  let formattedHolderDetails;
  let sectionTitle;

  if (holderDetails.registrationType === RegistrationHolderTypes.individual) {
    formattedHolderDetails = formatIndividualHolder(holderDetails);
    sectionTitle = 'Personal Details';
  } else if (holderDetails.registrationType === RegistrationHolderTypes.organisation) {
    formattedHolderDetails = formatOrganisationHolder(holderDetails);
    sectionTitle = 'Organisation Details';
  } else {
    console.error('Invalid registration type: ', holderDetails.registrationType);
    return;
  }

  let subSections = { title: sectionTitle, details: [] };
  subSections.details.push({
    text: ItemRegistrationHolderPage.registrationTypeLabel,
    value: capitalizeFirstLetter(holderDetails.registrationType),
  });

  subSections.details.push(...formattedHolderDetails);

  return subSections;
};

const formatOrganisationHolder = (holderDetails) => {
  const formattedMailingAddress = holderDetails.isSameAddress
    ? 'Same as registered business address'
    : formatAddress(holderDetails.mailingAddress);

  const orgDetails = [
    {
      text: ItemRegistrationHolderPage.organisationSearchLabel,
      value: holderDetails?.organisation?.organisationName,
    },
    {
      text: ItemRegistrationHolderPage.registeredBusinessNameLabel,
      value: holderDetails?.organisation?.selectedBusinessName,
    },
    {
      text: ItemRegistrationHolderPage.registeredBusinessAddressLabel,
      value: formatAddress(holderDetails.address),
    },
    {
      text: ItemRegistrationHolderPage.mailingAddressLabel,
      value: formattedMailingAddress,
    },
    {
      text: ItemRegistrationHolderPage.emailAddressLabel,
      value: holderDetails.emailAddress,
    },
    {
      text: ItemRegistrationHolderPage.phoneNumberLabel,
      value: holderDetails.phoneNumber?.formattedNumber,
    },
  ];

  return formatBlankFields(orgDetails, true);
};

const formatIndividualHolder = (holderDetails) => {
  let details = [];

  details.push({
    text: ItemRegistrationHolderPage.amIRegistrationHolderLabel,
    value: holderDetails.amIRegistrationHolder
      ? ItemRegistrationHolderPage.individualHolderUseMyDetailsLabel
      : ItemRegistrationHolderPage.individualHolderUseDifferentDetailsLabel,
  });

  const formattedMailingAddress = holderDetails.isSameAddress
    ? 'Same as residential address'
    : formatAddress(holderDetails.mailingAddress);

  details.push(
    { text: ItemRegistrationHolderPage.holderFirstNameLabel, value: holderDetails.firstName },
    { text: ItemRegistrationHolderPage.holderMiddleNameLabel, value: holderDetails.middleName },
    { text: ItemRegistrationHolderPage.holderLastNameLabel, value: holderDetails.lastName },
    { text: ItemRegistrationHolderPage.emailAddressLabel, value: holderDetails.emailAddress },
    { text: ItemRegistrationHolderPage.phoneNumberLabel, value: holderDetails.phoneNumber?.formattedNumber },
    { text: ItemRegistrationHolderPage.residentialAddressLabel, value: formatAddress(holderDetails.address) },
    { text: ItemRegistrationHolderPage.mailingAddressLabel, value: formattedMailingAddress },
  );

  return details;
};

const formatOngoingContact = (applicantDetails, holderDetails) => {
  if (!holderDetails?.ongoingContact) return [];

  const contact = holderDetails.ongoingContact;

  const { email, mobileNumber, landlineNumber } = getContactDetailsSubmission(applicantDetails, holderDetails);

  const subSectionDetails = [
    {
      text: ItemRegistrationHolderPage.ongoingContactTypeLabel,
      value: contact.contactType && OngoingTypes[contact.contactType]?.label,
    },
    {
      text: ItemRegistrationHolderPage.emailAddressLabel,
      value: email,
    },
    {
      text: ItemRegistrationHolderPage.phoneNumberLabel,
      value: mobileNumber || landlineNumber,
    },
  ];

  return { title: 'Ongoing contact', details: formatBlankFields(subSectionDetails, true) };
};

const formatItemsSection = (plantItems) => {
  let sections = [];

  let workingLocationSection = { title: 'Working location', subSections: [] };
  let locationDetails = { title: 'Plant item details', details: [] };
  locationDetails.details.push({
    text: PlantItemDetailsPage.locationAddressOfItemLabel,
    value: formatAddress(plantItems.location),
  });

  if (plantItems.reasonToRegister) {
    locationDetails.details.push({
      text: PlantItemDetailsPage.reasonToRegisterToNSWLabel,
      value: plantItems.reasonToRegister,
    });
  }

  workingLocationSection.subSections.push(locationDetails);

  sections.push(workingLocationSection);

  plantItems.items.forEach((items, index) => {
    switch (items.itemDetails.itemType) {
      case RefData.plantItemType.boiler:
        sections.push(formatBoilerSection(items, index));
        break;
      case RefData.plantItemType.pressureVessel:
        sections.push(formatPressureVesselSection(items, index));
        break;
      case RefData.plantItemType.lift:
        sections.push(formatLiftSection(items, index));
        break;
      case RefData.plantItemType.escalator:
      case RefData.plantItemType.movingWalkway:
        sections.push(formatEscalatorSection(items, index));
        break;
      case RefData.plantItemType.amusementDevice:
        sections.push(formatAmusementDeviceSection(items, index));
        break;
      case RefData.plantItemType.mobileCrane:
        sections.push(formatMobileCraneSection(items, index));
        break;
      case RefData.plantItemType.towerCrane:
        sections.push(formatTowerCraneSection(items, index));
        break;
      case RefData.plantItemType.buildingMaintenanceUnit:
        sections.push(formatBuildingMaintenanceUnitSection(items, index));
        break;
      case RefData.plantItemType.concretePlacingBoom:
        sections.push(formatConcretePlacingBoomSection(items, index));
        break;
    }
  });

  return sections;
};

const getItemTitle = (plantItem, index) => {
  return `${(index + 1).toString().padStart(2, '0')} ${plantItem?.itemDetails?.itemTypeText}`;
};

const formatAssociatedDesignSubSection = (associatedDesign, itemType) => {
  let subSections = { title: 'Associated design registration', details: [] };
  let designBeforeDate = PlantItemDetailsPage.associatedDesign.designedBeforeDateLabel;

  if (itemType == RefData.plantItemType.concretePlacingBoom) {
    designBeforeDate = `${PlantItemDetailsPage.associatedDesign.concretePlacingBoomDesignBeforeDateLabel} 
    <br/> ${PlantItemDetailsPage.associatedDesign.concreatePlacingBoomDesignBeforeDateMobileLabel} 
    <br/> ${PlantItemDetailsPage.associatedDesign.concreatePlacingBoomDesignBeforeDateFixedLabel}`;
  }

  subSections.details.push({ text: designBeforeDate, value: associatedDesign.designedBeforeRegistrationRequirement });

  if (associatedDesign.designedBeforeRegistrationRequirement === 'no') {
    subSections.details.push({
      text: PlantItemDetailsPage.associatedDesign.designRegisteredStateLabel,
      value: associatedDesign.state,
    });
    subSections.details.push({
      text: PlantItemDetailsPage.associatedDesign.designRegistrationNumberLabel,
      value: associatedDesign.registrationNumber,
    });
  }

  return subSections;
};

const formatItemDetailsSubsection = (itemType, itemDetails) => {
  let subSections = { title: 'Plant item details', details: [] };
  switch (itemType) {
    case RefData.plantItemType.boiler:
      subSections.details.push({ text: PlantItemDetailsPage.itemDetails.boilerTypeLabel, value: itemDetails.subType });
      break;
    case RefData.plantItemType.pressureVessel:
      subSections.details.push({
        text: PlantItemDetailsPage.itemDetails.pressureVesselTypeLabel,
        value: itemDetails.subType,
      });
      break;
    case RefData.plantItemType.lift:
      subSections.details.push({ text: PlantItemDetailsPage.itemDetails.liftTypeLabel, value: itemDetails.subType });
      break;
    case RefData.plantItemType.amusementDevice:
      subSections.details.push({
        text: PlantItemDetailsPage.itemDetails.amusementDeviceTypeLabel,
        value: itemDetails.subType,
      });
      break;
    default:
      break;
  }

  if (itemDetails?.otherSubType) {
    subSections.details.push({ text: PlantItemDetailsPage.itemDetails.otherType, value: itemDetails?.otherSubType });
  }

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.manufacturerLabel,
    value: itemDetails.manufacturer,
  });

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.modelNameOrNumberLabel,
    value: itemDetails.modelNameOrNumber,
  });

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.yearOfManufactureLabel,
    value: itemDetails.yearOfManufacture,
  });

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.serialNumberLabel,
    value: itemDetails.serialNumber,
  });

  if (itemDetails.itemType !== RefData.plantItemType.lift) {
    subSections.details.push({
      text: PlantItemDetailsPage.itemDetails.locationTypeLabel,
      value: itemDetails.locationType,
    });
  }

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.specificLocationLabel,
    value: itemDetails.specificLocation,
  });

  subSections.details.push({
    text: PlantItemDetailsPage.itemDetails.uniqueIdentifierLabel,
    value: itemDetails.uniqueIdentifier,
  });

  if (itemDetails.itemType === RefData.plantItemType.lift) {
    subSections.details.push({
      text: PlantItemDetailsPage.itemDetails.liftAccessibilityLabel,
      value: itemDetails.accessibilityAtLocation,
    });
  }

  return subSections;
};

const formatBoilerSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };
  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.hazardLevelLabel,
    value: plantItem.relatedDetails.hazardLevel,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.designPressureLabel,
    value: plantItem.relatedDetails.designPressure + ' kPag',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.designTemperatureLabel,
    value: plantItem.relatedDetails.designTemperature + ' &deg;C',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.maxVolumeLabel,
    value: plantItem.relatedDetails.maxVolume + ' L',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.outputUnitLabel,
    value: plantItem.relatedDetails.outputValue + ' ' + plantItem.relatedDetails.outputUnit,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.boiler.attendanceLabel,
    value: plantItem.relatedDetails.attendance,
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatPressureVesselSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.numberOfChambersLabel,
    value: plantItem.relatedDetails.numberOfChambers,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.hazardLevelLabel,
    value: plantItem.relatedDetails.hazardLevel,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.volumeLabel,
    value: plantItem.relatedDetails.volume + ' L',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.designPressureLabel,
    value: plantItem.relatedDetails.designPressure + ' KPag',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.designTemperatureLabel,
    value:
      plantItem.relatedDetails.minDesignTemp +
      ' ' +
      PlantItemDetailsPage.relatedDetails.pressureVessel.minTemperatureSuffix +
      ' ' +
      plantItem.relatedDetails.maxDesignTemp +
      ' ' +
      PlantItemDetailsPage.relatedDetails.pressureVessel.maxTemperatureSuffix,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.pressureVessel.chamberContentsLabel,
    value: plantItem.relatedDetails.chamberContents,
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatLiftSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.driveSuspensionTypeLabel,
    value: plantItem.relatedDetails.driveSuspensionType,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.isMRLLiftLabel,
    value: plantItem.relatedDetails.isMRLLift,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.carriageTypeLabel,
    value: plantItem.relatedDetails.carriageType,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.controlTypeLabel,
    value: plantItem.relatedDetails.controlType,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.maxSpeedLabel,
    value: plantItem.relatedDetails.maxSpeed + ' m/s',
  });

  if (
    plantItem.relatedDetails.operatingParams === 'maxRatedLoad' ||
    plantItem.relatedDetails.operatingParams === 'both'
  ) {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.lift.maxRatedLoadLabel,
      value: plantItem.relatedDetails.maxRatedLoad + ' Kg',
    });
  }

  if (
    plantItem.relatedDetails.operatingParams === 'maxPassengers' ||
    plantItem.relatedDetails.operatingParams === 'both'
  ) {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.lift.maxPassengersLabel,
      value: plantItem.relatedDetails.maxPassengers,
    });
  }

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.maxTravelLabel,
    value: plantItem.relatedDetails.maxTravel + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.lift.forConstructionLabel,
    value: plantItem.relatedDetails.forConstruction,
  });

  if (plantItem.relatedDetails.forConstruction === 'yes') {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.lift.inUncompliedConditionLabel,
      value: plantItem.relatedDetails.inUncompliedCondition,
    });

    if (plantItem.relatedDetails.inUncompliedCondition === 'yes') {
      relatedDetails.details.push({
        text: PlantItemDetailsPage.relatedDetails.lift.additionalControlMeasureLabel,
        value: plantItem.relatedDetails.additionalControlMeasure,
      });
    }

    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.lift.hasBeenInspectedLabel,
      value: plantItem.relatedDetails.hasBeenInspected,
    });
  }

  section.subSections.push(relatedDetails);
  return section;
};

const formatEscalatorSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.escalator.maxSpeedLabel,
    value: plantItem.relatedDetails.maxSpeed + ' m/s',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.escalator.maxAngleLabel,
    value: plantItem.relatedDetails.maxAngle + ' degrees',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.escalator.maxTravelLabel,
    value: plantItem.relatedDetails.maxTravel + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.escalator.maxWidthLabel,
    value: plantItem.relatedDetails.maxWidth + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.escalator.maxPassengersLabel,
    value: plantItem.relatedDetails.maxPassengers,
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatAmusementDeviceSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.nameOrDescriptionLabel,
    value: plantItem.relatedDetails.name,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.deviceClassLabel,
    value: plantItem.relatedDetails.deviceClass,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.mobilityTypeTabel,
    value: plantItem.relatedDetails.mobilityType,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.electricalInstallationLabel,
    value: plantItem.relatedDetails.electricalInstallation,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.operatingPowerLabel,
    value: plantItem.relatedDetails.operatingPower,
  });

  if (plantItem.relatedDetails.otherOperatingPower) {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.amusementDevice.otherOperatingPowerLabel,
      value: plantItem.relatedDetails.otherOperatingPower,
    });
  }

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxSpeedLabel,
    value: plantItem.relatedDetails.maxSpeed + ' m/s',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxAccelerationLabel,
    value: plantItem.relatedDetails.maxAcceleration + ' m/s&sup2;',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxResolvingSpeedLabel,
    value: plantItem.relatedDetails.maxResolvingSpeed + ' rpm',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxHeightPatronLabel,
    value: plantItem.relatedDetails.maxHeightPatron + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxPatronSupportUnitsLabel,
    value: plantItem.relatedDetails.maxPatronSupportUnits,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxAdultPatronsPerSupportLabel,
    value: plantItem.relatedDetails.maxAdultPatronsPerSupport,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.maxChildPatronsPerSupportLabel,
    value: plantItem.relatedDetails.maxChildPatronsPerSupport,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.minAgeUnAccompaniedPatronLabel,
    value: plantItem.relatedDetails.minAgeUnAccompaniedPatron + ' years',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.minAgeAccompaniedPatronLabel,
    value: plantItem.relatedDetails.minAgeAccompaniedPatron + ' years',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.amusementDevice.minPatronHeightLabel,
    value: plantItem.relatedDetails.minPatronHeight + ' cm',
  });

  section.subSections.push(relatedDetails);

  if (plantItem.relatedDetails.electricalInstallation === 'yes') {
    let electricalInspectionSection = {
      title: 'Electrical inspection certificate',
      details: formatInspectionDetails(plantItem.relatedDetails.electricalInspectionDetails),
    };

    section.subSections.push(electricalInspectionSection);
  }

  let engineersInspectionSection = {
    title: 'Engineers inspection certificate',
    details: formatInspectionDetails(plantItem.relatedDetails.engineerInspectionDetails),
  };

  section.subSections.push(engineersInspectionSection);

  return section;
};

const formatMobileCraneSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.mobileCrane.typeOfMobileCraneLabel,
    value: plantItem.relatedDetails.typeOfMobileCrane,
  });

  if (plantItem.relatedDetails.otherTypeOfMobileCrane) {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.mobileCrane.otherTypeOfMobileCraneLabel,
      value: plantItem.otherTypeOfMobileCrane,
    });
  }

  if (plantItem.relatedDetails.typeOfMobileCrane === 'Vehicle loading crane') {
    relatedDetails.details.push({
      text: PlantItemDetailsPage.relatedDetails.mobileCrane.modelDetailsLabel,
      value: plantItem.relatedDetails.modelDetails,
    });
  }

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.mobileCrane.maxRadiusLabel,
    value: plantItem.relatedDetails.maxRadius + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.mobileCrane.maxRatedCapacityLabel,
    value: plantItem.relatedDetails.maxRatedCapacity + ' t',
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatTowerCraneSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.towerCrane.typeOfTowerCraneLabel,
    value: plantItem.relatedDetails.typeOfTowerCrane,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.towerCrane.maxRadiusLabel,
    value: plantItem.relatedDetails.maxRadius + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.towerCrane.maxHeightLabel,
    value: plantItem.relatedDetails.maxHeight + ' m',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.towerCrane.maxRatedCapacityLabel,
    value: plantItem.relatedDetails.maxRatedCapacity + ' t',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.towerCrane.worksiteRegisteredLabel,
    value: plantItem.relatedDetails.worksiteRegistered,
  });

  section.subSections.push(relatedDetails);

  if (plantItem.relatedDetails.worksiteRegistered === 'yes') {
    let tcFoundationInspectionSection = {
      title: 'Tower crane foundation design statement',
      details: formatInspectionDetails(
        plantItem.relatedDetails.inspectionDetails,
        PlantItemDetailsPage.relatedDetails.towerCraneFoundationInspection,
      ),
    };

    section.subSections.push(tcFoundationInspectionSection);
  }

  return section;
};

const formatBuildingMaintenanceUnitSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.buildingMaintenanceUnit.safeWorkingLoadLabel,
    value: plantItem.relatedDetails.safeWorkingLoad + ' kg',
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.buildingMaintenanceUnit.maxWorkingHeightLabel,
    value: plantItem.relatedDetails.maxWorkingHeight + ' m',
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatConcretePlacingBoomSection = (plantItem, index) => {
  let section = { title: getItemTitle(plantItem, index), subSections: [] };
  section.subSections.push(formatItemDetailsSubsection(plantItem.itemDetails.itemType, plantItem.itemDetails));
  section.subSections.push(formatAssociatedDesignSubSection(plantItem?.designDetails, plantItem.itemDetails.itemType));

  let relatedDetails = { title: 'Related item details', details: [] };

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.concretePlacingUnit.concretePlacingUnitTypeLabel,
    value: plantItem.relatedDetails.concretePlacingUnitType,
  });

  relatedDetails.details.push({
    text: PlantItemDetailsPage.relatedDetails.concretePlacingUnit.maxBoomLengthLabel,
    value: plantItem.relatedDetails.maxBoomLength + ' m',
  });

  section.subSections.push(relatedDetails);
  return section;
};

const formatInspectionDetails = (
  inspectionDetails,
  labels = PlantItemDetailsPage.relatedDetails.inspectionDocumentUpload,
) => {
  let details = [];

  details.push({
    text: labels.competentPersonLabel,
    value: inspectionDetails.competentPerson,
  });

  details.push({
    text: labels.inspectionDateLabel,
    value: inspectionDetails.inspectionDate,
  });

  details.push({
    text: labels.anySpecialConditionsLabel,
    value: inspectionDetails.anySpecialConditions,
  });

  if (inspectionDetails.anySpecialConditions === 'yes') {
    details.push({
      text: labels.describeConditionsLabel,
      value: inspectionDetails.specialConditions,
    });
  }

  details.push({
    text: labels.supportingDocumentsLabel,
    value: formatUploadedFiles(inspectionDetails.uploadedFiles),
  });

  return details;
};

const formatUploadedFiles = (uploadedFiles) => {
  let summaryFiles = '';
  if (Array.isArray(uploadedFiles)) {
    uploadedFiles.forEach((file) => {
      summaryFiles += `<br/>${file.name} (${formatBytes(file.size)})`;
    });
  }

  return summaryFiles;
};

function formatBytes(bytes, decimals) {
  if (bytes == 0) return '0 Bytes';
  var k = 1024,
    dm = decimals || 2,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
